"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { useAuthStore } from "@/lib/auth-store";
import { useDelegationStore } from "@/lib/delegation-store";
import { useTranslations } from "next-intl";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";
import {
  User,
  HeartPulse,
  Pill,
  FlaskConical,
  Users,
  ShieldCheck,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";

type ProfileNavSection = {
  heading: string;
  items: { href: string; label: string; icon: LucideIcon }[];
};

function isActivePath(pathname: string, href: string) {
  return pathname === href || pathname.startsWith(`${href}/`);
}

function ProfileSectionLinks({
  section,
  variant,
  onNavigate,
}: {
  section: ProfileNavSection;
  variant: "sidebar" | "mobile";
  onNavigate?: () => void;
}) {
  const pathname = usePathname();

  if (variant === "mobile") {
    return (
      <section className="space-y-2">
        <h2 className="px-1 text-xs font-semibold uppercase tracking-[0.08em] text-muted-foreground">
          {section.heading}
        </h2>
        <div className="grid grid-cols-2 gap-2">
          {section.items.map(({ href, label, icon: Icon }) => {
            const isActive = isActivePath(pathname, href);

            return (
              <Link
                key={href}
                href={href}
                onClick={onNavigate}
                aria-current={isActive ? "page" : undefined}
                className={cn(
                  "flex min-h-20 flex-col items-start justify-center gap-2 rounded-xl border px-4 py-4 text-left text-sm font-medium transition-colors",
                  isActive
                    ? "border-primary/30 bg-primary/10 text-foreground shadow-sm"
                    : "border-border bg-card text-foreground hover:bg-muted/60"
                )}
              >
                <Icon className="h-5 w-5 shrink-0" />
                <span className="leading-snug">{label}</span>
              </Link>
            );
          })}
        </div>
      </section>
    );
  }

  return (
    <section className="space-y-1">
      <h2 className="px-4 pb-1 pt-3 text-xs font-semibold uppercase tracking-[0.08em] text-muted-foreground">
        {section.heading}
      </h2>
      <div className="flex flex-col gap-1">
        {section.items.map(({ href, label, icon: Icon }) => {
          const isActive = isActivePath(pathname, href);

          return (
            <Link
              key={href}
              href={href}
              onClick={onNavigate}
              aria-current={isActive ? "page" : undefined}
              className={cn(
                "flex items-center gap-3 rounded-xl px-4 py-3 text-[15px] font-medium transition-colors",
                isActive
                  ? "bg-primary/10 text-foreground shadow-sm ring-1 ring-primary/15"
                  : "text-muted-foreground hover:bg-muted/60 hover:text-foreground"
              )}
            >
              <Icon className="h-5 w-5 shrink-0" />
              <span className="leading-snug">{label}</span>
            </Link>
          );
        })}
      </div>
    </section>
  );
}

function useProfileNavigationSections() {
  const t = useTranslations("nav");
  const isOwnProfile = !useDelegationStore((state) => state.onBehalfOfUserId);

  return [
    {
      heading: t("healthSection"),
      items: [
        {
          href: "/profile/medications",
          label: t(isOwnProfile ? "myMedications" : "medications"),
          icon: Pill,
        },
        {
          href: "/profile/conditions",
          label: t(isOwnProfile ? "myConditions" : "conditions"),
          icon: HeartPulse,
        },
        {
          href: "/profile/labs",
          label: t(isOwnProfile ? "myLabResults" : "labResults"),
          icon: FlaskConical,
        },
        {
          href: "/profile/access",
          label: t("sharedAccess"),
          icon: Users,
        },
      ],
    },
    {
      heading: t("accountSection"),
      items: [
        {
          href: "/profile/details",
          label: t("personalDetails"),
          icon: User,
        },
        {
          href: "/profile/security",
          label: t("signInSecurity"),
          icon: ShieldCheck,
        },
      ],
    },
  ];
}

export function ProfileSidebarContent({
  onNavigate,
}: {
  onNavigate?: () => void;
}) {
  const t = useTranslations("nav");
  const { user } = useAuthStore();
  const sections = useProfileNavigationSections();
  const profileLabel = t("myProfile");
  const identityLabel = user?.displayName || user?.email || null;

  return (
    <>
      <div className="mb-3 px-4">
        <p className="text-xs font-semibold uppercase tracking-[0.08em] text-muted-foreground">
          {profileLabel}
        </p>
        {identityLabel && (
          <p className="mt-1 truncate text-base font-semibold">
            {identityLabel}
          </p>
        )}
        {user?.email && user?.displayName && (
          <p className="mt-1 truncate text-sm text-muted-foreground">
            {user.email}
          </p>
        )}
      </div>
      <Separator className="mb-1" />
      <nav className="flex flex-col gap-2">
        {sections.map((section) => (
          <ProfileSectionLinks
            key={section.heading}
            section={section}
            variant="sidebar"
            onNavigate={onNavigate}
          />
        ))}
      </nav>
    </>
  );
}

export function ProfileSidebar() {
  return (
    <aside className="flex flex-col gap-1">
      <ProfileSidebarContent />
    </aside>
  );
}

export function ProfileMobileNav() {
  const sections = useProfileNavigationSections();

  return (
    <nav className="space-y-4 rounded-2xl border bg-background p-4">
      {sections.map((section) => (
        <ProfileSectionLinks
          key={section.heading}
          section={section}
          variant="mobile"
        />
      ))}
    </nav>
  );
}
